import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import 'react-datepicker/dist/react-datepicker.css';
import '../datepicker-custom.css';
import { useContext, useEffect } from "react";
import { ProfileContext } from "pages/Profile";
import { useSelector } from 'react-redux';

const DatePickerPopperContainer = ({ children }) => createPortal(children, document.body);

const parseRawDateInput = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isClearButtonClick = (event) => {
  const target = event?.target;
  return Boolean(target?.closest?.('.react-datepicker__close-icon'));
};

const isValidDate = (value) => value instanceof Date && !Number.isNaN(value.getTime());

export default function DatePickers({ userPathway, onUserPathwayChange }) {
  const { profile } = useContext(ProfileContext);
  const { user: authUser } = useSelector(x => x.auth);
  const [startDate, setStartDate] = useState(userPathway.startedAt ? new Date(userPathway.startedAt) : null);
  const [endDate, setEndDate] = useState(userPathway.completedAt ? new Date(userPathway.completedAt) : null);
  const [draftStartDate, setDraftStartDate] = useState(startDate);
  const [draftEndDate, setDraftEndDate] = useState(endDate);
  const [startRawInput, setStartRawInput] = useState('');
  const [endRawInput, setEndRawInput] = useState('');
  const [reloadFlag, setReloadFlag] = useState(0);
  const [editingStart, setEditingStart] = useState(false);
  const [editingEnd, setEditingEnd] = useState(false);

  // Officer check: use acl field for Officer, or self
  const canEdit = authUser.user.acl === 'Officer' || authUser.user._id === profile._id;

  const url = process.env.REACT_APP_URL;

  const handleStartDateChange = async (date) => {
    setStartDate(date);
    try {
      const response = await fetch(`${url}/userpathway/${userPathway.user}/${userPathway.pathwayId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authUser.token}`
          },
          body: JSON.stringify({ startedAt: date ? date.toISOString() : null })
        }
      );
      if (!response.ok) throw new Error('Failed to update start date');
      onUserPathwayChange?.({ startedAt: date ? date.toISOString() : null });
      setReloadFlag(f => f + 1);
      setDraftStartDate(date);
      toast.success('Start date updated successfully', { position: 'bottom-left' });
    } catch (err) {
      console.error('Error updating start date:', err);
      toast.error('Failed to update start date', { position: 'bottom-left' });
    }
  };

  const handleEndDateChange = async (date) => {
    setEndDate(date);
    try {
      const response = await fetch(`${url}/userpathway/${userPathway.user}/${userPathway.pathwayId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authUser.token}`
          },
          body: JSON.stringify({ completedAt: date ? date.toISOString() : null })
        }
      );
      if (!response.ok) throw new Error('Failed to update end date');
      onUserPathwayChange?.({ completedAt: date ? date.toISOString() : null });
      setReloadFlag(f => f + 1);
      setDraftEndDate(date);
      toast.success('End date updated successfully', { position: 'bottom-left' });
    } catch (err) {
      console.error('Error updating end date:', err);
      toast.error('Failed to update end date', { position: 'bottom-left' });
    }
  };
  useEffect(() => {
    setStartDate(userPathway.startedAt ? new Date(userPathway.startedAt) : null);
    setEndDate(userPathway.completedAt ? new Date(userPathway.completedAt) : null);
    setDraftStartDate(userPathway.startedAt ? new Date(userPathway.startedAt) : null);
    setDraftEndDate(userPathway.completedAt ? new Date(userPathway.completedAt) : null);
  }, [userPathway.startedAt, userPathway.completedAt]);

  useEffect(() => {
    async function reloadUserPathway() {
      try {
        const getRes = await fetch(`${url}/userpathway/${userPathway.user}/${userPathway.pathwayId}`,
          {
            headers: { 'Authorization': `Bearer ${authUser.token}` }
          }
        );
        if (getRes.ok) {
          const updated = await getRes.json();
          setStartDate(updated.startedAt ? new Date(updated.startedAt) : null);
          setEndDate(updated.completedAt ? new Date(updated.completedAt) : null);
          setDraftStartDate(updated.startedAt ? new Date(updated.startedAt) : null);
          setDraftEndDate(updated.completedAt ? new Date(updated.completedAt) : null);
          onUserPathwayChange?.({
            startedAt: updated.startedAt || null,
            completedAt: updated.completedAt || null,
          });
        }
      } catch (err) {
        // Optionally handle error
      }
    }
    reloadUserPathway();
    // eslint-disable-next-line
  }, [reloadFlag]);

  const openStartEditor = () => {
    if (!canEdit) return;
    setDraftStartDate(startDate);
    setStartRawInput('');
    setEditingStart(true);
  };

  const openEndEditor = () => {
    if (!canEdit) return;
    setDraftEndDate(endDate);
    setEndRawInput('');
    setEditingEnd(true);
  };

  const commitStartDraft = async () => {
    const fallbackFromRaw = parseRawDateInput(startRawInput);
    const toSave = draftStartDate ?? fallbackFromRaw;
    await handleStartDateChange(toSave || null);
    setEditingStart(false);
    setStartRawInput('');
  };

  const commitEndDraft = async () => {
    const fallbackFromRaw = parseRawDateInput(endRawInput);
    const toSave = draftEndDate ?? fallbackFromRaw;
    await handleEndDateChange(toSave || null);
    setEditingEnd(false);
    setEndRawInput('');
  };

  return (
    <div className="my-pathway-dates">
      <div
        className="my-pathway-date-row"
        onClick={() => {
          if (editingStart) return;
          openStartEditor();
        }}
        title={canEdit ? 'Click to edit start date' : ''}
      >
        <span className="my-pathway-date-label">
          <i className="bi bi-play-fill" />
          Started
        </span>
        {editingStart && canEdit ? (
          <DatePicker
            selected={draftStartDate instanceof Date && !isNaN(draftStartDate) ? draftStartDate : null}
            onChange={(date) => {
              setDraftStartDate(date || null);
              if (!date) setStartRawInput('');
              if (isValidDate(date)) {
                handleStartDateChange(date);
                setEditingStart(false);
                setStartRawInput('');
              }
            }}
            onChangeRaw={(event) => setStartRawInput(event?.target?.value || '')}
            dateFormat="yyyy-MM-dd"
            className="form-control custom-datepicker-input"
            placeholderText="Select start date"
            isClearable
            shouldCloseOnSelect={false}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                commitStartDraft();
              }
              if (event.key === 'Escape') {
                setEditingStart(false);
                setDraftStartDate(startDate);
                setStartRawInput('');
              }
            }}
            onClickOutside={(event) => {
              if (isClearButtonClick(event)) return;
              commitStartDraft();
            }}
            autoFocus
            popperPlacement="top-start"
            popperProps={{ strategy: 'fixed' }}
            popperContainer={DatePickerPopperContainer}
            showYearDropdown
            showMonthDropdown
            dropdownMode="select"
            popperClassName="custom-datepicker-popper"
            open={editingStart}
          />
        ) : (
          <span className="my-pathway-date-value">
            {startDate instanceof Date && !isNaN(startDate) ? startDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}
          </span>
        )}
      </div>

      <div
        className="my-pathway-date-row"
        onClick={() => {
          if (editingEnd) return;
          openEndEditor();
        }}
        title={canEdit ? 'Click to edit completion date' : ''}
      >
        <span className="my-pathway-date-label">
          <i className="bi bi-check2-circle" />
          Completed
        </span>
        {editingEnd && canEdit ? (
          <DatePicker
            selected={draftEndDate instanceof Date && !isNaN(draftEndDate) ? draftEndDate : null}
            onChange={(date) => {
              setDraftEndDate(date || null);
              if (!date) setEndRawInput('');
              if (isValidDate(date)) {
                handleEndDateChange(date);
                setEditingEnd(false);
                setEndRawInput('');
              }
            }}
            onChangeRaw={(event) => setEndRawInput(event?.target?.value || '')}
            dateFormat="yyyy-MM-dd"
            className="form-control custom-datepicker-input"
            placeholderText="Select completion date"
            isClearable
            shouldCloseOnSelect={false}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                commitEndDraft();
              }
              if (event.key === 'Escape') {
                setEditingEnd(false);
                setDraftEndDate(endDate);
                setEndRawInput('');
              }
            }}
            onClickOutside={(event) => {
              if (isClearButtonClick(event)) return;
              commitEndDraft();
            }}
            autoFocus
            popperPlacement="top-start"
            popperProps={{ strategy: 'fixed' }}
            popperContainer={DatePickerPopperContainer}
            showYearDropdown
            showMonthDropdown
            dropdownMode="select"
            popperClassName="custom-datepicker-popper"
            open={editingEnd}
          />
        ) : (
          <span className="my-pathway-date-value">
            {endDate instanceof Date && !isNaN(endDate) ? endDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}
          </span>
        )}
      </div>
    </div>
  )
}